import React, {
  useState,
  useEffect,
} from "react";
import rcsLogo from "../assets/rcslogo.png";
import {
  useParams,
  useNavigate,
} from "react-router-dom";

import {
  ArrowLeft,
  Wallet,
  Landmark,
  User,
  CircleDollarSign,
  CalendarDays,
  BadgeCheck,
  Clock3,
  XCircle,
  Pencil,
  Save,
  Trash2,
  Phone,
  MapPin,
  CreditCard,
  FileText,
  Cake,
  ShieldCheck,
} from "lucide-react";

import "../Styles/ChequeDetails.css";

import api from "../api";

import { toast } from "react-toastify";

/* ========================================= */
/* DATE FORMAT */
/* ========================================= */

const chequeTrackerFormatLongDate = (
  dateString
) => {

  if (!dateString)
    return "N/A";

  const date = new Date(
    dateString + "T00:00:00"
  );

  if (isNaN(date))
    return "Invalid Date";

  const day = date.getDate();

  const month =
    date.toLocaleString(
      "en-US",
      {
        month: "long",
      }
    );

  const year =
    date.getFullYear();

  const getOrdinalSuffix = (
    num
  ) => {

    if (
      num > 3 &&
      num < 21
    )
      return "th";

    switch (num % 10) {

      case 1:
        return "st";

      case 2:
        return "nd";

      case 3:
        return "rd";

      default:
        return "th";
    }
  };

  return `${day}${getOrdinalSuffix(
    day
  )} ${month}, ${year}`;
};

/* ========================================= */
/* COMPONENT */
/* ========================================= */

const ChequeDetails = () => {

  const {
    cheque_number,
  } = useParams();

  const navigate =
    useNavigate();

  const [
    chequeTrackerCheque,
    setChequeTrackerCheque,
  ] = useState(null);

  const [
    chequeTrackerEditing,
    setChequeTrackerEditing,
  ] = useState(false);

  const [
    chequeTrackerDetails,
    setChequeTrackerDetails,
  ] = useState({
    address: "",
    phone_number: "",
    id_type:
      "National ID",
    id_number: "",
    date_of_issue: "",
    date_of_expiry: "",
    date_of_birth: "",
    notes: "",
  });

  /* ========================================= */
  /* FETCH */
  /* ========================================= */

  useEffect(() => {

    const fetchChequeTrackerData =
      async () => {

        try {

          const mainCheque =
            await api.getChequeByNumber(
              cheque_number
            );

          if (mainCheque) {

            setChequeTrackerCheque(
              mainCheque
            );
          }

          const extra =
            await api.getChequeDetails(
              cheque_number
            );

          if (extra) {

            setChequeTrackerDetails({
              address:
                extra.address ||
                "",

              phone_number:
                extra.phone_number ||
                "",

              id_type:
                extra.id_type ||
                "National ID",

              id_number:
                extra.id_number ||
                "",

              date_of_issue:
                extra.date_of_issue
                  ? extra.date_of_issue.split(
                      "T"
                    )[0]
                  : "",

              date_of_expiry:
                extra.date_of_expiry
                  ? extra.date_of_expiry.split(
                      "T"
                    )[0]
                  : "",

              date_of_birth:
                extra.date_of_birth
                  ? extra.date_of_birth.split(
                      "T"
                    )[0]
                  : "",

              notes:
                extra.notes ||
                "",
            });
          }

        } catch (error) {

          console.error(
            error
          );

          toast.error(
            "Failed to load cheque."
          );
        }
      };

    fetchChequeTrackerData();

  }, [cheque_number]);

  /* ========================================= */
  /* CHANGE */
  /* ========================================= */

  const handleChequeTrackerChange =
    (e) => {

      setChequeTrackerDetails({
        ...chequeTrackerDetails,
        [e.target.name]:
          e.target.value,
      });
    };

  /* ========================================= */
  /* SAVE */
  /* ========================================= */

  const handleChequeTrackerSave =
    async () => {

      try {

        const existing =
          await api.getChequeDetails(
            cheque_number
          );

        const payload = {
          ...chequeTrackerDetails,
        };

        if (!existing) {

          await api.saveChequeDetails(
            cheque_number,
            payload
          );

        } else {

          await api.editChequeDetails(
            cheque_number,
            payload
          );
        }

        toast.success(
          "Cheque details updated."
        );

        setChequeTrackerEditing(
          false
        );

      } catch (error) {

        console.error(
          error
        );

        toast.error(
          "Failed to save details."
        );
      }
    };

  /* ========================================= */
  /* DELETE */
  /* ========================================= */

  const handleChequeTrackerDelete =
    async () => {

      const confirmDelete =
        window.confirm(
          "Delete this cheque permanently?"
        );

      if (!confirmDelete)
        return;

      try {

        await api.deleteCheque(
          chequeTrackerCheque.cheque_number
        );

        toast.success(
          "Cheque deleted."
        );

        navigate(
          "/cheque-tracker"
        );

      } catch (error) {

        console.error(
          error
        );

        toast.error(
          "Delete failed."
        );
      }
    };

  /* ========================================= */
  /* STATUS STYLE */
  /* ========================================= */

  const chequeTrackerStatusClass =
    (status) => {

      switch (
        status?.toLowerCase()
      ) {

        case "unpresented":
          return "cheque-tracker-status-unpresented";

        case "deposited":
          return "cheque-tracker-status-deposited";

        case "cleared":
          return "cheque-tracker-status-cleared";

        case "cancelled":
          return "cheque-tracker-status-cancelled";

        default:
          return "";
      }
    };

if (!chequeTrackerCheque) {

  return (

    <div className="cheque-tracker-loading-screen">

      <div className="cheque-tracker-loader-container">

        {/* GLOW */}

        <div className="cheque-tracker-loader-glow" />

        {/* SPINNER */}

        <div className="cheque-tracker-loader-spinner">

          {/* OUTER RING */}

          <div className="cheque-tracker-loader-ring" />

          {/* INNER RING */}

          <div className="cheque-tracker-loader-ring-secondary" />

          {/* LOGO */}

          <div className="cheque-tracker-loader-logo">

<img
  src={rcsLogo}
  alt="RCS Logo"
/>

          </div>

        </div>

        {/* TEXT */}

        <div className="cheque-tracker-loader-text">

          <h2>
            Loading Cheque
          </h2>

          <p>
          Hang Tight, We are fetching
          the details for this cheque! 
          </p>

        </div>

      </div>

    </div>
  );
}

  return (

    <div className="cheque-tracker-details-page">

      {/* HERO */}

      <div className="cheque-tracker-hero-card">

        <div className="cheque-tracker-hero-overlay" />

        <button
          className="cheque-tracker-back-btn"
          onClick={() =>
            navigate(
              "/cheque-tracker"
            )
          }
        >

          <ArrowLeft size={18} />

          Back To Tracker

        </button>

        <div className="cheque-tracker-hero-content">

          <div className="cheque-tracker-hero-left">

            <div className="cheque-tracker-hero-pill">
              CHEQUE TRACKER
            </div>

            <h1>
              Cheque Details
            </h1>

            <p>
              Monitor cheque
              activity,
              verification,
              identification
              and financial
              processing
              information in
              real-time.
            </p>

          </div>

          {/* CHEQUE VISUAL */}

          <div className="cheque-tracker-hero-preview">

            <div className="cheque-tracker-floating-status cheque-tracker-status-deposited">
              Deposited
            </div>

            <div className="cheque-tracker-preview-card">

              <span className="cheque-tracker-preview-bank">
                RCS Financial
              </span>

              <h2>
                $
                {Number(
                  chequeTrackerCheque.amount
                ).toLocaleString()}
              </h2>

              <div className="cheque-tracker-preview-row">

                <div>

                  <span>
                    PAYEE
                  </span>

                  <strong>
                    {
                      chequeTrackerCheque.payee
                    }
                  </strong>

                </div>

                <div>

                  <span>
                    CHEQUE #
                  </span>

                  <strong>
                    {
                      chequeTrackerCheque.cheque_number
                    }
                  </strong>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* MAIN */}

      <div className="cheque-tracker-layout">

        {/* LEFT */}

        <div className="cheque-tracker-main-column">

          {/* CHEQUE INFO */}

          <div className="cheque-tracker-glass-section">

            <div className="cheque-tracker-section-header">

              <div>

                <h3>
                  Cheque Information
                </h3>

                <p>
                  Main cheque
                  transaction
                  details.
                </p>

              </div>

              <div
                className={`cheque-tracker-status-badge ${chequeTrackerStatusClass(
                  chequeTrackerCheque.status
                )}`}
              >

                {chequeTrackerCheque.status ===
                  "Cleared" && (
                  <BadgeCheck size={15} />
                )}

                {chequeTrackerCheque.status ===
                  "Deposited" && (
                  <Clock3 size={15} />
                )}

                {chequeTrackerCheque.status ===
                  "Cancelled" && (
                  <XCircle size={15} />
                )}

                {chequeTrackerCheque.status ===
                  "Unpresented" && (
                  <Wallet size={15} />
                )}

                {
                  chequeTrackerCheque.status
                }

              </div>

            </div>

            <div className="cheque-tracker-info-grid">

              <div className="cheque-tracker-info-card">
                <Wallet size={18} />

                <span>
                  Cheque Number
                </span>

                <strong>
                  {
                    chequeTrackerCheque.cheque_number
                  }
                </strong>
              </div>

              <div className="cheque-tracker-info-card">
                <Landmark size={18} />

                <span>
                  Bank Drawn
                </span>

                <strong>
                  {
                    chequeTrackerCheque.bank_drawn
                  }
                </strong>
              </div>

              <div className="cheque-tracker-info-card">
                <User size={18} />

                <span>
                  Payer
                </span>

                <strong>
                  {
                    chequeTrackerCheque.payer
                  }
                </strong>
              </div>

              <div className="cheque-tracker-info-card">
                <User size={18} />

                <span>
                  Payee
                </span>

                <strong>
                  {
                    chequeTrackerCheque.payee
                  }
                </strong>
              </div>

              <div className="cheque-tracker-info-card cheque-tracker-money-card">
                <CircleDollarSign size={18} />

                <span>
                  Amount
                </span>

                <strong>
                  $
                  {Number(
                    chequeTrackerCheque.amount
                  ).toLocaleString()}
                </strong>
              </div>

              <div className="cheque-tracker-info-card cheque-tracker-money-card">
                <ShieldCheck size={18} />

                <span>
                  Admin Charge
                </span>

                <strong>
                  $
                  {Number(
                    chequeTrackerCheque.admin_charge
                  ).toLocaleString()}
                </strong>
              </div>

              <div className="cheque-tracker-info-card cheque-tracker-money-card">
                <CircleDollarSign size={18} />

                <span>
                  Net To Payee
                </span>

                <strong>
                  $
                  {Number(
                    chequeTrackerCheque.net_to_payee
                  ).toLocaleString()}
                </strong>
              </div>

              <div className="cheque-tracker-info-card">
                <CalendarDays size={18} />

                <span>
                  Date Posted
                </span>

                <strong>
                  {chequeTrackerFormatLongDate(
                    chequeTrackerCheque.date_posted
                  )}
                </strong>
              </div>

            </div>

          </div>

          {/* ADDITIONAL */}

          <div className="cheque-tracker-glass-section">

            <div className="cheque-tracker-section-header">

              <div>

                <h3>
                  Additional Information
                </h3>

                <p>
                  Identification,
                  verification
                  and supporting
                  notes.
                </p>

              </div>

              {!chequeTrackerEditing ? (

                <button
                  className="cheque-tracker-action-btn cheque-tracker-edit-btn"
                  onClick={() =>
                    setChequeTrackerEditing(
                      true
                    )
                  }
                >

                  <Pencil size={16} />

                  Edit

                </button>

              ) : (

                <button
                  className="cheque-tracker-action-btn cheque-tracker-save-btn"
                  onClick={
                    handleChequeTrackerSave
                  }
                >

                  <Save size={16} />

                  Save

                </button>
              )}

            </div>

            <div className="cheque-tracker-form-grid">

              <div className="cheque-tracker-input-card">

                <label>
                  <MapPin size={15} />
                  Address
                </label>

                <input
                  type="text"
                  name="address"
                  value={
                    chequeTrackerDetails.address
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <Phone size={15} />
                  Phone Number
                </label>

                <input
                  type="text"
                  name="phone_number"
                  value={
                    chequeTrackerDetails.phone_number
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <CreditCard size={15} />
                  ID Type
                </label>

                <select
                  name="id_type"
                  value={
                    chequeTrackerDetails.id_type
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                >

                  <option>
                    National ID
                  </option>

                  <option>
                    Passport
                  </option>

                  <option>
                    Driver's License
                  </option>

                </select>

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <CreditCard size={15} />
                  ID Number
                </label>

                <input
                  type="text"
                  name="id_number"
                  value={
                    chequeTrackerDetails.id_number
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <CalendarDays size={15} />
                  Date Of Issue
                </label>

                <input
                  type="date"
                  name="date_of_issue"
                  value={
                    chequeTrackerDetails.date_of_issue
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

                <small>
                  {chequeTrackerFormatLongDate(
                    chequeTrackerDetails.date_of_issue
                  )}
                </small>

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <CalendarDays size={15} />
                  Date Of Expiry
                </label>

                <input
                  type="date"
                  name="date_of_expiry"
                  value={
                    chequeTrackerDetails.date_of_expiry
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

                <small>
                  {chequeTrackerFormatLongDate(
                    chequeTrackerDetails.date_of_expiry
                  )}
                </small>

              </div>

              <div className="cheque-tracker-input-card">

                <label>
                  <Cake size={15} />
                  Date Of Birth
                </label>

                <input
                  type="date"
                  name="date_of_birth"
                  value={
                    chequeTrackerDetails.date_of_birth
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

                <small>
                  {chequeTrackerFormatLongDate(
                    chequeTrackerDetails.date_of_birth
                  )}
                </small>

              </div>

              <div className="cheque-tracker-input-card cheque-tracker-full-width-card">

                <label>
                  <FileText size={15} />
                  Notes
                </label>

                <textarea
                  name="notes"
                  rows="5"
                  value={
                    chequeTrackerDetails.notes
                  }
                  onChange={
                    handleChequeTrackerChange
                  }
                  disabled={
                    !chequeTrackerEditing
                  }
                />

              </div>

            </div>

          </div>

        </div>

        {/* SIDEBAR */}

        <div className="cheque-tracker-sidebar-column">

          <div className="cheque-tracker-sidebar-card">

            <h4>
              Quick Actions
            </h4>

            <button
              className="cheque-tracker-sidebar-btn"
              onClick={() =>
                setChequeTrackerEditing(
                  true
                )
              }
            >

              <Pencil size={16} />

              Edit Cheque

            </button>

            <button
              className="cheque-tracker-sidebar-btn cheque-tracker-sidebar-delete-btn"
              onClick={
                handleChequeTrackerDelete
              }
            >

              <Trash2 size={16} />

              Delete Cheque

            </button>

          </div>

        </div>

      </div>

    </div>
  );
};

export default ChequeDetails;